import React, { useState } from "react";
import { Bookmark, ChevronDown, ChevronUp, MapPin, Calendar, Building2, ExternalLink, Activity } from "lucide-react";

const OpportunitiesDataView = ({ applications, loading }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <div className="bg-card rounded-2xl border border-border p-12 text-center mt-6">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Bookmark size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-bold text-foreground">No data found</h3>
        <p className="text-sm text-muted-foreground mt-1">You haven't uploaded/applied to any opportunities yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5 mt-6 animate-fade-in shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-primary" /> Uploaded Opportunities
        </h3>
        <div className="bg-muted px-3 py-1 rounded-full text-xs font-bold text-muted-foreground">
          {applications.length} Records
        </div>
      </div>
      
      <div className="space-y-3">
        {applications.map((app) => {
          const oppTitle = app.Opportunity?.title || "Unknown Opportunity";
          const oppOrg = app.Opportunity?.org || "Unknown Org";
          const isExpanded = expandedId === app.id;
          
          let statusColor = "text-amber-500 bg-amber-500/10 border-amber-500/20";
          if (app.status === 'accepted') statusColor = "text-emerald-500 bg-emerald-500/10 border-emerald-500/20";
          if (app.status === 'declined') statusColor = "text-rose-500 bg-rose-500/10 border-rose-500/20";

          // Extract questions and answers
          const questions = app.Opportunity?.questions || [];
          const qaList = questions.map(q => {
            const questionText = q.text || q.label || q;
            const answer = app.answers?.[questionText] || "—";
            return { question: questionText, answer };
          });

          return (
            <div key={app.id} className="rounded-xl border border-border bg-muted/30 overflow-hidden transition-all">
              <div 
                className="p-4 flex flex-col sm:flex-row sm:items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : app.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusColor} uppercase tracking-wider`}>
                      {app.status}
                    </span>
                  </div>
                  <h4 className="text-sm font-display font-bold text-foreground">{oppTitle}</h4>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {oppOrg}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {new Date(app.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="mt-3 sm:mt-0 flex items-center justify-end">
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              </div>
              
              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-3 border-t border-border bg-card/50">
                  <h5 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5 text-primary" /> Application Details
                  </h5>
                  
                  {app.message && (
                    <div className="mb-4">
                      <p className="text-[10px] uppercase font-bold text-muted-foreground mb-1">Your Message</p>
                      <p className="text-sm text-foreground bg-muted p-3 rounded-lg border border-border">{app.message}</p>
                    </div>
                  )}

                  {qaList.length > 0 && (
                    <div className="space-y-3 mb-4">
                      <p className="text-[10px] uppercase font-bold text-muted-foreground mb-1">Form Responses</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {qaList.map((qa, idx) => (
                          <div key={idx} className="bg-muted p-3 rounded-lg border border-border">
                            <p className="text-xs font-medium text-muted-foreground mb-1">{qa.question}</p>
                            <p className="text-sm font-semibold text-foreground">{qa.answer}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-4 pt-4 border-t border-border/50">
                    <button className="h-8 px-4 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">
                      View Original Post
                    </button>
                    {(app.portfolioLink || app.cv) && (
                      <button className="h-8 px-3 rounded-lg border border-border text-xs text-muted-foreground hover:bg-muted transition-colors flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5" /> View Attachments
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OpportunitiesDataView;
